import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import useAccessibleDialog from '../../hooks/useAccessibleDialog';
import useShippingRequest from '../../hooks/useShippingRequest';
import distributorService from '../../services/distributorService';
import { usd } from './cashMoney';
import { readCashReviews, validMerchantDay, validMerchantReference } from './cashReviewContract';
import {
  cashDepositMethod,
  depositStateLabel,
  readCashDeposit,
  sameCashDeposit,
  confirmedChargeDeposit,
} from './cashDepositContract';
import CashMerchantForms, { emptyMerchantReview } from './CashMerchantForms';
import CashWireDepositReview from './CashWireDepositReview';

const unwrap = (response) => response?.data?.data;
export default function CashDepositReview({ distributorId, deposit, onClose, onUpdated }) {
  return cashDepositMethod(deposit) === 'wire' ? (
    <CashWireDepositReview
      distributorId={distributorId}
      deposit={deposit}
      onClose={onClose}
      onUpdated={onUpdated}
    />
  ) : confirmedChargeDeposit(deposit) ? (
    <CashConfirmedChargeReview
      distributorId={distributorId}
      deposit={deposit}
      onClose={onClose}
      onUpdated={onUpdated}
    />
  ) : (
    <CashMerchantDepositReview
      distributorId={distributorId}
      deposit={deposit}
      onClose={onClose}
      onUpdated={onUpdated}
    />
  );
}
function CashConfirmedChargeReview({ distributorId, deposit, onClose, onUpdated }) {
  const t = useT();
  const [receipt, setReceipt] = useState(deposit),
    [busy, setBusy] = useState(false);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const pending = useRef(false);
  const { begin } = useShippingRequest(`cash-charge-review:${distributorId}:${deposit._id}`);
  const { dialogRef, titleId } = useAccessibleDialog({ open: true, onClose, closeDisabled: busy });
  const refresh = async () => {
    if (pending.current) return;
    const request = begin();
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      readCashDeposit(receipt);
      const value = readCashDeposit(
        unwrap(await distributorService.refreshAdminCashPayment(distributorId, deposit._id))
      );
      if (!request.isCurrent()) return;
      if (!sameCashDeposit(value, deposit)) throw new Error('Changed request');
      setReceipt(value);
      onUpdated?.(value);
      setNotice('Payment status refreshed. The available balance reflects confirmed card charges.');
    } catch {
      if (request.isCurrent())
        setError('Funding status could not be refreshed. No new payment was sent.');
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  return (
    <div className="me-cash-review-overlay">
      <section
        className="me-cash me-cash-review-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="me-cash-heading">
          <div>
            <p>{t('Cash funding review')}</p>
            <h2 id={titleId}>
              {usd(receipt.amountCents)} · {t(depositStateLabel(receipt))}
            </h2>
          </div>
          <button type="button" disabled={busy} onClick={onClose}>
            {t('Close review')}
          </button>
        </div>
        <p>{t('Request reference: {reference}', { reference: deposit._id })}</p>
        <p>
          {t(
            'Credit is added after QuickBooks confirms the successful card charge. Refresh this request if its outcome is pending.'
          )}
        </p>
        {error && <p role="alert">{t(error)}</p>}
        {notice && <p role="status">{t(notice)}</p>}
        {busy && <p role="status">{t('Checking your cash funding request...')}</p>}
        <button type="button" disabled={busy} onClick={refresh}>
          {t('Refresh funding status')}
        </button>
      </section>
    </div>
  );
}
function CashMerchantDepositReview({ distributorId, deposit, onClose, onUpdated }) {
  const t = useT();
  const [receipt, setReceipt] = useState(deposit),
    [reviews, setReviews] = useState(null);
  const [draft, setDraft] = useState(emptyMerchantReview),
    [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [retry, setRetry] = useState(0);
  const pending = useRef(false),
    unresolved = useRef(null);
  const { begin, version } = useShippingRequest(`cash-review:${distributorId}:${deposit._id}`);
  const { dialogRef, titleId } = useAccessibleDialog({
    open: true,
    onClose,
    closeDisabled: busy || uncertain,
  });
  const getReviews = async (request) => {
    const [funding, adverse] = await Promise.all([
      distributorService.adminCashMerchantFunding(distributorId, deposit._id, {
        signal: request.signal,
      }),
      distributorService.adminCashAdverseReview(distributorId, deposit._id, {
        signal: request.signal,
      }),
    ]);
    return readCashReviews(unwrap(funding), unwrap(adverse), deposit);
  };
  useEffect(() => {
    const request = begin();
    setReviews(null);
    setError('');
    getReviews(request)
      .then((value) => {
        if (request.isCurrent()) setReviews(value);
      })
      .catch(() => {
        if (request.isCurrent())
          setError('Merchant evidence could not load. Reload before reviewing.');
      });
    // An explicit read refresh must retain an unresolved mutation for comparison.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [begin, version, retry]);
  const recover = async () => {
    if (pending.current) return;
    const request = begin();
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      const value = await getReviews(request);
      if (!request.isCurrent()) return;
      const prior = unresolved.current,
        proof =
          prior &&
          value[prior.kind][prior.kind === 'funding' ? 'merchantFunding' : 'merchantAdverseReview'];
      setReviews(value);
      if (prior && proof?.requestKey !== prior.requestKey) {
        setError(
          'The merchant update is still unconfirmed. Contact MedEffects before recording another review.'
        );
        return;
      }
      unresolved.current = null;
      setUncertain(false);
      setDraft(emptyMerchantReview());
      setNotice('Merchant evidence refreshed. No new payment was sent.');
    } catch {
      if (request.isCurrent())
        setError('Merchant evidence could not be recovered. Do not repeat the update.');
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  const submit = async (event, kind) => {
    event.preventDefault();
    if (
      pending.current ||
      uncertain ||
      !reviews ||
      !(kind === 'funding' ? reviews.funding.canConfirm : reviews.adverse.canReview)
    )
      return;
    if (
      kind === 'funding'
        ? !validMerchantReference(draft.depositReference) ||
          !validMerchantDay(draft.fundedDate) ||
          !draft.funded ||
          !draft.included
        : !validMerchantReference(draft.reviewReference) || !draft.clean
    )
      return setError('Enter a valid merchant reference and confirm the evidence shown.');
    const requestKey = window.crypto?.randomUUID?.();
    if (!requestKey)
      return setError('A secure request key is unavailable. Reload before continuing.');
    const view = reviews[kind];
    const input = {
      requestKey,
      expectedAttemptRevision: view.expectedAttemptRevision,
      realmId: view.realmId,
      transactionId: view.transactionId,
      amountCents: view.amountCents,
      currency: 'USD',
      ...(kind === 'funding'
        ? {
            depositReference: draft.depositReference,
            fundedDate: draft.fundedDate,
            confirmedFunded: true,
            confirmedPaymentIncluded: true,
          }
        : { reviewReference: draft.reviewReference, confirmedNoRefundsReturnsOrDisputes: true }),
    };
    const request = begin();
    pending.current = true;
    unresolved.current = { kind, requestKey };
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (kind === 'funding')
        await distributorService.confirmAdminCashMerchantFunding(distributorId, deposit._id, input);
      else
        await distributorService.confirmAdminCashAdverseReview(distributorId, deposit._id, input);
      if (!request.isCurrent()) return;
      const value = await getReviews(request);
      if (!request.isCurrent()) return;
      const proof = value[kind][kind === 'funding' ? 'merchantFunding' : 'merchantAdverseReview'];
      if (proof?.requestKey !== requestKey) throw new Error('Unconfirmed merchant update');
      unresolved.current = null;
      setReviews(value);
      setDraft(emptyMerchantReview());
      setNotice('Merchant evidence saved. Refresh funding status to check settlement.');
      onUpdated?.();
    } catch {
      if (request.isCurrent()) {
        setUncertain(true);
        setError(
          'The merchant update could not be confirmed. Recover the saved evidence before continuing.'
        );
      }
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  const refreshStatus = async () => {
    if (pending.current || uncertain) return;
    const request = begin();
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const value = readCashDeposit(
        unwrap(await distributorService.refreshAdminCashPayment(distributorId, deposit._id))
      );
      if (!request.isCurrent()) return;
      if (!sameCashDeposit(value, deposit)) throw new Error('Wrong deposit');
      setReceipt(value);
      const evidence = await getReviews(request);
      if (!request.isCurrent()) return;
      setReviews(evidence);
      setDraft(emptyMerchantReview());
      setNotice('Funding status refreshed. Available funds are controlled by verified settlement.');
      onUpdated?.();
    } catch {
      if (request.isCurrent())
        setError('Funding status could not be refreshed. No new payment was sent.');
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  return (
    <div className="me-cash-review-overlay">
      <section
        className="me-cash me-cash-review-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="me-cash-heading">
          <div>
            <p>{t('Cash funding review')}</p>
            <h2 id={titleId}>
              {usd(receipt.amountCents)} · {t(depositStateLabel(receipt))}
            </h2>
          </div>
          <button type="button" disabled={busy || uncertain} onClick={onClose}>
            {t('Close review')}
          </button>
        </div>
        <p>{t('Request reference: {reference}', { reference: deposit._id })}</p>
        <p className="me-cash-note">
          {t(
            'Merchant confirmations do not add funds by themselves. The server must independently verify settlement and accounting before crediting the balance.'
          )}
        </p>
        {error && <p role="alert">{t(error)}</p>}
        {notice && <p role="status">{t(notice)}</p>}
        {busy && <p role="status">{t('Checking merchant evidence…')}</p>}
        {uncertain ? (
          <button type="button" disabled={busy} onClick={recover}>
            {t('Recover merchant evidence')}
          </button>
        ) : !reviews ? (
          <button type="button" disabled={busy} onClick={() => setRetry((value) => value + 1)}>
            {t('Reload merchant evidence')}
          </button>
        ) : (
          <>
            <p>
              {t('Processor transaction: {reference}', {
                reference: reviews.funding.transactionId || '—',
              })}
            </p>
            <CashMerchantForms
              reviews={reviews}
              draft={draft}
              busy={busy}
              uncertain={uncertain}
              edit={(key, value) => {
                if (!pending.current) {
                  setDraft((old) => ({ ...old, [key]: value }));
                  setError('');
                }
              }}
              submit={submit}
            />
            <div className="me-cash-actions">
              <button type="button" disabled={busy} onClick={recover}>
                {t('Reload merchant evidence')}
              </button>
              <button type="button" disabled={busy} onClick={refreshStatus}>
                {t('Refresh funding status')}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
